import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

type Section = { h: string; p: string[] };
type Faq = { q: string; a: string };

export default function AdminBlogEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) navigate("/admin/login", { replace: true });
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (!id || !isAdmin) return;
    supabase.from("blog_posts").select("*").eq("id", id).maybeSingle().then(({ data, error }) => {
      if (error) { toast.error(error.message); return; }
      if (!data) { toast.error("Post not found"); navigate("/admin/blog"); return; }
      setForm(data);
      setLoading(false);
    });
  }, [id, isAdmin, navigate]);

  if (authLoading || loading || !form) return <Layout><div className="container-page py-16">Loading…</div></Layout>;

  const update = (patch: any) => setForm((f: any) => ({ ...f, ...patch }));
  const sections: Section[] = Array.isArray(form.sections) ? form.sections : [];
  const faqs: Faq[] = Array.isArray(form.faqs) ? form.faqs : [];

  const save = async (publish?: boolean) => {
    setSaving(true);
    try {
      const status = publish === undefined ? form.status : publish ? "published" : "draft";
      const payload: any = {
        slug: form.slug,
        title: form.title,
        excerpt: form.excerpt,
        category: form.category,
        read_time: form.read_time,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        intro: form.intro,
        sections: form.sections,
        faqs: form.faqs,
        hero_image_url: form.hero_image_url ?? null,
        hero_image_alt: form.hero_image_alt ?? null,
        status,
        published_at: status === "published" ? (form.published_at ?? new Date().toISOString()) : null,
      };
      const { error } = await supabase.from("blog_posts").update(payload).eq("id", form.id);
      if (error) throw error;
      toast.success(publish === true ? "Published" : publish === false ? "Unpublished" : "Saved");
      if (publish !== undefined) update({ status, published_at: status === "published" ? new Date().toISOString() : null });
    } catch (err: any) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <Seo title={`Edit · ${form.title}`} description="Admin editor" />
      <section className="container-page py-10 max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/admin/blog" className="text-sm text-accentInk hover:underline">← Back to all posts</Link>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => save()} disabled={saving}>Save draft</Button>
            {form.status === "published" ? (
              <Button variant="outline" onClick={() => save(false)} disabled={saving}>Unpublish</Button>
            ) : (
              <Button onClick={() => save(true)} disabled={saving}>Publish</Button>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => update({ title: e.target.value })} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => update({ slug: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => update({ category: e.target.value })} /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Read time</Label><Input value={form.read_time} onChange={(e) => update({ read_time: e.target.value })} /></div>
            <div><Label>Meta title</Label><Input value={form.meta_title ?? ""} onChange={(e) => update({ meta_title: e.target.value })} /></div>
          </div>
          <div>
            <Label>Excerpt</Label>
            <Textarea rows={2} value={form.excerpt} onChange={(e) => update({ excerpt: e.target.value })} />
          </div>
          <div>
            <Label>Meta description</Label>
            <Textarea rows={2} value={form.meta_description ?? ""} onChange={(e) => update({ meta_description: e.target.value })} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Hero image URL</Label><Input value={form.hero_image_url ?? ""} onChange={(e) => update({ hero_image_url: e.target.value })} /></div>
            <div><Label>Hero image alt</Label><Input value={form.hero_image_alt ?? ""} onChange={(e) => update({ hero_image_alt: e.target.value })} /></div>
          </div>
          {form.hero_image_url && (
            <img src={form.hero_image_url} alt={form.hero_image_alt ?? ""} className="w-full max-w-md rounded border border-border" />
          )}
          <div>
            <Label>Intro</Label>
            <Textarea rows={4} value={form.intro} onChange={(e) => update({ intro: e.target.value })} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2"><Label>Sections</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => update({ sections: [...sections, { h: "New section", p: [""] }] })}>Add section</Button>
            </div>
            <div className="space-y-4">
              {sections.map((s, i) => (
                <div key={i} className="border border-border rounded p-3 space-y-2">
                  <Input value={s.h} onChange={(e) => { const n = [...sections]; n[i] = { ...s, h: e.target.value }; update({ sections: n }); }} />
                  <Textarea rows={4} value={s.p.join("\n\n")} onChange={(e) => { const n = [...sections]; n[i] = { ...s, p: e.target.value.split("\n\n") }; update({ sections: n }); }} />
                  <Button type="button" size="sm" variant="ghost" onClick={() => update({ sections: sections.filter((_, k) => k !== i) })}>Remove</Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2"><Label>FAQs</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => update({ faqs: [...faqs, { q: "", a: "" }] })}>Add FAQ</Button>
            </div>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <div key={i} className="border border-border rounded p-3 space-y-2">
                  <Input placeholder="Question" value={f.q} onChange={(e) => { const n = [...faqs]; n[i] = { ...f, q: e.target.value }; update({ faqs: n }); }} />
                  <Textarea rows={2} placeholder="Answer" value={f.a} onChange={(e) => { const n = [...faqs]; n[i] = { ...f, a: e.target.value }; update({ faqs: n }); }} />
                  <Button type="button" size="sm" variant="ghost" onClick={() => update({ faqs: faqs.filter((_, k) => k !== i) })}>Remove</Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
